import { RefObject, useLayoutEffect } from 'react';

/** Read by `.ChatSearchBar__Input` as its right padding. */
export const TRAILING_WIDTH_PROPERTY = '--chat-search-trailing-width';

/**
 * Keeps the field's text clear of the block on its right (counter · divider ·
 * arrows), which sits inside the field's border but is not the DS Input's
 * `trailingIcon`: that slot is `aria-hidden` and `pointer-events: none`, so the
 * arrows and "Intenta de nuevo" could be neither clicked nor heard there.
 *
 * Writes the block's width to a custom property on the field instead of a
 * `style` prop, re-measured whenever the block resizes (the counter's copy
 * changes width with every state).
 *
 * @param fieldRef - The field wrapper the property is set on.
 * @param trailingRef - The block on the right whose width is reserved.
 */
export default function useTrailingInset(
  fieldRef: RefObject<HTMLElement>,
  trailingRef: RefObject<HTMLElement>,
): void {
  useLayoutEffect(() => {
    const field = fieldRef.current;
    const trailing = trailingRef.current;

    if (!field || !trailing) return undefined;

    const measure = () =>
      field.style.setProperty(
        TRAILING_WIDTH_PROPERTY,
        `${trailing.offsetWidth}px`,
      );

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(trailing);

    return () => observer.disconnect();
  }, [fieldRef, trailingRef]);
}
